import React, { useState, useCallback } from 'react';
import { z } from 'zod';
import { AuthEventKeys, AuthEvents } from '../events';
import { Typography, TextField, Button, CircularProgress } from '@mui/material';
import { EventBus } from '@/shared/utils';
import { Auth, getDictErrorMessage } from '@/shared/i18n';
import { useSearchParams } from 'next/navigation';

const emailSchema = z.string().email("L'indirizzo email non è valido");

type ForgotPasswordFormProps = {
  eventBus: EventBus<AuthEvents>;

  dict: Auth;
};

function ForgotPasswordForm({ eventBus, dict }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const searchParams = useSearchParams();
  const prefilledEmail = searchParams.get('email');

  const isUserMigration = !!prefilledEmail && prefilledEmail.length > 0;

  // Gestione degli errori tramite eventBus
  const subscribeToForgotPasswordEvents = useCallback(() => {
    const handleSuccess = () => {
      setError(null);
      setLoading(false);
    };
    const handleError = (error: Error) => {
      const errorMessage = getDictErrorMessage(dict.auth_errors, error.message);
      setError(errorMessage);
      setLoading(false);
    };

    eventBus.on(AuthEventKeys.forgotPassword.success, handleSuccess);
    eventBus.on(AuthEventKeys.forgotPassword.error, handleError);
    return () => {
      eventBus.off(AuthEventKeys.forgotPassword.success, handleSuccess);
      eventBus.off(AuthEventKeys.forgotPassword.error, handleError);
    };
  }, [eventBus, dict]);

  React.useEffect(() => {
    setEmail(prefilledEmail && prefilledEmail?.length > 0 ? prefilledEmail : '');
  }, []);

  React.useEffect(() => {
    const cleanup = subscribeToForgotPasswordEvents();
    return cleanup;
  }, [subscribeToForgotPasswordEvents]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }
    setError(null);
    setLoading(true);
    eventBus.emit(AuthEventKeys.forgotPassword.do, email);
  };

  const handleShowLogin = () => {
    eventBus.emit(AuthEventKeys.login.view, undefined);
  };

  return (
    <section className="flex z-10 flex-col self-center max-w-[600px] px-6 py-8 md:px-16 md:py-12 rounded-xl bg-paper">
      <div className="flex flex-col gap-2 text-center">
        <Typography variant="mac-sans-h3" component="h1" color="textPrimary">
          {isUserMigration
            ? dict.update_password?.title || 'Bentornato'
            : dict.forgot_password?.title || 'Hai dimenticato la password?'}
        </Typography>
        <Typography variant="body-500" component="p" color="textSecondary">
          {isUserMigration
            ? dict.update_password?.description || 'Bentornato'
            : dict.forgot_password?.description ||
              'Non ti preoccupare, succede anche ai migliori! Inserisci il tuo indirizzo email per resettarla.'}
        </Typography>
      </div>
      <div className="mt-12">
        <form className="flex flex-col" onSubmit={handleSubmit}>
          <TextField
            placeholder={dict.forgot_password?.email_placeholder || 'Inserisci la tua e-mail'}
            variant="outlined"
            size="medium"
            label={
              <Typography variant="s2">
                {dict.forgot_password?.email_label || 'Indirizzo e-mail'}
              </Typography>
            }
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
            helperText={error || ''}
            disabled={loading || isUserMigration}
          />
          <div className="flex flex-col w-full gap-4 mt-8">
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              className="w-full"
              disabled={loading || !email.trim()}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Typography variant="button-l">
                  {dict.forgot_password?.button_text || 'Invia link'}
                </Typography>
              )}
            </Button>
            {/*  <Button
              size="large"
              variant="text"
              color="primary"
              className="w-full gap-2"
              onClick={handleShowLogin}
              disabled={loading}
            >
              <Typography variant="button-l">
                <ArrowBackFilled /> {dict.forgot_password?.back_to_login || 'Torna al login'}
              </Typography>
            </Button> */}
          </div>
        </form>
      </div>
    </section>
  );
}

export default ForgotPasswordForm;
